import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, PenLine } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import useLenis from "@/hooks/useLenis";
import api, { mediaUrl } from "@/lib/api";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); }
  catch { return ""; }
};
const readTime = (body) => Math.max(1, Math.round((body || "").split(/\s+/).filter(Boolean).length / 200));
const extUrl = (a) => a.external_url || a.cta_url || a.link || "";
const srcName = (a) => a.meta || a.source || "";
const isExternal = (a) => !(a.body || "").trim() && !!extUrl(a);
// Press clippings with no body open the original source; everything else opens the reader.
function ArticleLink({ a, className, children, ...rest }) {
  if (isExternal(a)) return <a href={extUrl(a)} target="_blank" rel="noopener noreferrer" className={className} {...rest}>{children}</a>;
  return <Link to={`/articles/${a.id}`} className={className} {...rest}>{children}</Link>;
}
export const fetchAllArticles = () =>
  Promise.all([api.get("/content?type=post"), api.get("/content?type=article")]).then(([p, a]) => {
    const seen = new Set();
    return [...p.data, ...a.data].filter((x) => (seen.has(x.id) ? false : (seen.add(x.id), true)));
  });

function SliderCard({ a }) {
  const img = mediaUrl(a);
  return (
    <ArticleLink
      a={a}
      data-testid={`slider-article-${a.id}`}
      className="group relative shrink-0 w-[85vw] sm:w-[520px] md:w-[640px] snap-start overflow-hidden border border-imi-border bg-imi-surface hover:border-imi-gold/60 transition-colors"
    >
      <div className="aspect-[16/9] overflow-hidden bg-imi-elevated relative">
        {img ? (
          <img src={img} alt={a.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><PenLine size={40} className="text-imi-gold/25" /></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-imi-bg via-imi-bg/30 to-transparent" />
        <div className="absolute bottom-0 inset-x-0 p-6 md:p-8">
          {a.category && <span className="text-imi-gold text-[10px] uppercase tracking-[0.25em] border border-imi-gold/40 bg-imi-bg/70 backdrop-blur px-2.5 py-1">{a.category}</span>}
          <h3 className="mt-3 font-serif text-2xl md:text-3xl text-imi-text leading-tight group-hover:text-imi-goldlight transition-colors">{a.title}</h3>
          <div className="mt-2 flex items-center gap-3 text-imi-text/50 text-xs uppercase tracking-[0.15em]">
            <span>{fmtDate(a.created_at)}</span><span className="text-imi-gold/50">·</span><span>{isExternal(a) ? (srcName(a) || "Press") : `${readTime(a.body)} min read`}</span>
          </div>
        </div>
      </div>
    </ArticleLink>
  );
}

export default function ArticlesPage() {
  useLenis();
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState(null);
  const [cat, setCat] = useState("all");
  const railRef = useRef(null);

  useEffect(() => {
    fetchAllArticles().then((all) => {
      setItems([...all].sort((x, y) => (y.created_at || "").localeCompare(x.created_at || "")));
    }).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  const cats = useMemo(() => ["all", ...Array.from(new Set(items.map((i) => (i.category || "").toLowerCase().trim()).filter(Boolean)))], [items]);
  const filtered = cat === "all" ? items : items.filter((i) => (i.category || "").toLowerCase().trim() === cat);
  const sliderItems = items.filter((i) => i.featured).length > 0 ? items.filter((i) => i.featured) : items.slice(0, 5);
  const slide = (dir) => railRef.current?.scrollBy({ left: dir * (railRef.current.clientWidth * 0.8), behavior: "smooth" });

  return (
    <div className="bg-imi-bg text-imi-text min-h-screen">
      <Header />
      <main className="pt-28 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Link to="/" data-testid="back-home" className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-imi-text/50 hover:text-imi-gold transition-colors">
            <ArrowLeft size={16} /> Back home
          </Link>

          <Reveal className="mt-10 max-w-3xl">
            <p className="text-[11px] uppercase tracking-[0.35em] text-imi-gold mb-5">{settings?.articles_kicker || "Articles"}</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight">{settings?.articles_title || "Written from the inside out."}</h1>
            <p className="mt-5 text-imi-text/60 text-base md:text-lg leading-relaxed">
              {settings?.articles_subtitle || "Original writing on solitary confinement, mental health behind the walls, and the fight for reform. Choose what to read — every piece is free to share."}
            </p>
          </Reveal>
        </div>

        {sliderItems.length > 0 && (
          <div className="mt-14 relative" data-testid="articles-slider">
            <div ref={railRef} className="flex gap-5 md:gap-6 overflow-x-auto snap-x snap-mandatory px-6 md:px-10 pb-4 scrollbar-hide" style={{ scrollbarWidth: "none" }}>
              {sliderItems.map((a) => <SliderCard key={a.id} a={a} />)}
            </div>
            {sliderItems.length > 1 && (
              <div className="flex gap-2 px-6 md:px-10 mt-2">
                <button onClick={() => slide(-1)} data-testid="slider-prev" aria-label="Previous articles" className="h-11 w-11 border border-imi-border text-imi-text/60 hover:border-imi-gold hover:text-imi-gold transition-colors flex items-center justify-center"><ChevronLeft size={18} /></button>
                <button onClick={() => slide(1)} data-testid="slider-next" aria-label="Next articles" className="h-11 w-11 border border-imi-border text-imi-text/60 hover:border-imi-gold hover:text-imi-gold transition-colors flex items-center justify-center"><ChevronRight size={18} /></button>
              </div>
            )}
          </div>
        )}

        <div className="max-w-[1400px] mx-auto px-6 md:px-10 mt-16">
          {cats.length > 2 && (
            <div className="flex flex-wrap gap-2 mb-10" data-testid="articles-filters">
              {cats.map((c) => (
                <button key={c} onClick={() => setCat(c)} data-testid={`articles-filter-${c}`}
                  className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] border transition-colors ${cat === c ? "border-imi-gold text-imi-gold bg-imi-gold/5" : "border-imi-border text-imi-text/50 hover:border-imi-text/40"}`}
                >{c}</button>
              ))}
            </div>
          )}

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-testid="articles-grid">
            {filtered.map((a) => {
              const img = mediaUrl(a);
              return (
                <ArticleLink key={a.id} a={a} data-testid={`article-card-${a.id}`}
                  className="group border border-imi-border bg-imi-surface/40 overflow-hidden hover:border-imi-gold/50 transition-colors flex flex-col">
                  <div className="aspect-[16/10] bg-imi-elevated overflow-hidden relative">
                    {img ? (
                      <img src={img} alt={a.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><PenLine size={30} className="text-imi-gold/25" /></div>
                    )}
                    {a.category && <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.2em] text-imi-gold bg-imi-bg/80 backdrop-blur border border-imi-gold/30 px-2.5 py-1">{a.category}</span>}
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="text-imi-text/40 text-[10px] uppercase tracking-[0.25em]">{fmtDate(a.created_at)}{isExternal(a) ? (srcName(a) ? ` · ${srcName(a)}` : "") : (a.body || "").trim() ? ` · ${readTime(a.body)} min read` : ""}</div>
                    <h3 className="mt-3 font-serif text-xl md:text-2xl leading-tight group-hover:text-imi-goldlight transition-colors line-clamp-2">{a.title}</h3>
                    {(a.excerpt || a.subtitle) && <p className="mt-3 text-imi-text/55 text-sm leading-relaxed line-clamp-3 flex-1">{a.excerpt || a.subtitle}</p>}
                    <div className="mt-5 pt-4 border-t border-imi-border flex items-center justify-between text-[11px] uppercase tracking-[0.2em] text-imi-gold">
                      <span>{isExternal(a) ? "Read at source" : "Read article"}</span><ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </ArticleLink>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="py-24 text-center border border-imi-border" data-testid="articles-empty">
              <PenLine size={40} className="text-imi-gold/30 mx-auto mb-4" />
              <div className="font-serif text-xl mb-2">No articles {cat === "all" ? "yet" : `in "${cat}"`}</div>
              <p className="text-imi-text/50 text-sm">New writing is published from Admin → Content → Type: post.</p>
            </div>
          )}
        </div>
      </main>
      <Footer settings={settings} />
    </div>
  );
}
